import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Users, Calculator, X } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useFeatureFlagsStore } from '@/stores/featureFlagsStore';
import { localToday } from '@/lib/dateRange';

/**
 * MultiPersonExpenseDialog — record one expense split equally across N
 * people (employees and/or partners). Used by the Beneficiaries page
 * for shared payments like ID-card renewal fees, government fines,
 * group medical insurance, etc.
 *
 * Props:
 *   open           : boolean
 *   onOpenChange   : (boolean) => void
 *   beneficiaryId  : optional string — links every created expense back
 *                    to the source beneficiary
 *   defaultCategory: optional string — pre-fills the category field
 *                    (e.g. "ID Card Fee" if beneficiary linked to "ID Card")
 *   onCreated      : (batch) => void — fires after successful create
 */
export default function MultiPersonExpenseDialog({
  open, onOpenChange, beneficiaryId, defaultCategory = '', onCreated,
}) {
  const [employees, setEmployees] = useState([]);
  const [partners, setPartners] = useState([]);
  const [pickedEmployees, setPickedEmployees] = useState({});  // {id: true}
  const [pickedPartners, setPickedPartners] = useState({});
  const [category, setCategory] = useState(defaultCategory);
  const [totalAmount, setTotalAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [date, setDate] = useState(localToday());
  const [description, setDescription] = useState('');
  const [overridePerPerson, setOverridePerPerson] = useState(false);
  const [overrides, setOverrides] = useState({}); // {id: amount}
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  // iter593 — Hide partner section unless tenant explicitly opted in.
  const partnersEnabled = useFeatureFlagsStore(s => s.flags.enable_partner_entries);

  // Reset form when re-opened
  useEffect(() => {
    if (open) {
      setCategory(defaultCategory || '');
      setTotalAmount('');
      setPaymentMode('cash');
      setDate(localToday());
      setDescription('');
      setPickedEmployees({});
      setPickedPartners({});
      setOverridePerPerson(false);
      setOverrides({});
      setSearch('');
    }
  }, [open, defaultCategory]);

  // Load employees + partners once dialog opens
  useEffect(() => {
    if (!open) return;
    let active = true;
    (async () => {
      try {
        const [e, p] = await Promise.all([
          api.get('/employees').catch(() => ({ data: [] })),
          api.get('/partners').catch(() => ({ data: [] })),
        ]);
        if (!active) return;
        const emps = (Array.isArray(e.data) ? e.data : (e.data?.employees || []))
          .filter(x => x?.id)
          .map(x => ({ id: x.id, label: x.name || x.full_name || '—', branch_id: x.branch_id }));
        const ptrs = (Array.isArray(p.data) ? p.data : (p.data?.partners || []))
          .filter(x => x?.id)
          .map(x => ({ id: x.id, label: x.name || '—' }));
        setEmployees(emps);
        setPartners(ptrs);
      } catch { /* ignore */ }
    })();
    return () => { active = false; };
  }, [open]);

  const empCount = Object.values(pickedEmployees).filter(Boolean).length;
  const partnerCount = Object.values(pickedPartners).filter(Boolean).length;
  const total = empCount + partnerCount;
  const eachShare = useMemo(() => {
    const amt = parseFloat(totalAmount) || 0;
    return total > 0 ? Math.round((amt / total) * 100) / 100 : 0;
  }, [totalAmount, total]);

  const filteredEmployees = useMemo(() => {
    const s = search.trim().toLowerCase();
    return s ? employees.filter(e => e.label.toLowerCase().includes(s)) : employees;
  }, [employees, search]);
  const filteredPartners = useMemo(() => {
    const s = search.trim().toLowerCase();
    return s ? partners.filter(p => p.label.toLowerCase().includes(s)) : partners;
  }, [partners, search]);

  const togglePick = (kind, id) => {
    const setter = kind === 'employee' ? setPickedEmployees : setPickedPartners;
    setter(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const submit = async () => {
    const amt = parseFloat(totalAmount) || 0;
    if (!category.trim()) { toast.error('Category is required'); return; }
    if (total === 0) { toast.error('Pick at least one employee or partner'); return; }
    if (!overridePerPerson && amt <= 0) { toast.error('Enter total amount'); return; }

    const employeeIds = Object.entries(pickedEmployees).filter(([, v]) => v).map(([k]) => k);
    const partnerIds = Object.entries(pickedPartners).filter(([, v]) => v).map(([k]) => k);
    const orderedIds = [...employeeIds, ...partnerIds];

    let amounts;
    if (overridePerPerson) {
      amounts = orderedIds.map(id => Number(overrides[id] || 0));
      const sum = amounts.reduce((s, x) => s + x, 0);
      if (sum <= 0) { toast.error('Enter at least one per-person amount'); return; }
    }

    setSubmitting(true);
    try {
      const body = {
        category: category.trim(),
        payment_mode: paymentMode,
        date,
        description: description.trim(),
        employee_ids: employeeIds,
        partner_ids: partnerIds,
        beneficiary_id: beneficiaryId || null,
      };
      if (overridePerPerson) body.amounts = amounts;
      else body.total_amount = amt;
      const res = await api.post('/expenses/multi-person', body);
      toast.success(`Recorded ${res.data?.people_count || total} expenses · SAR ${(res.data?.total_amount || amt).toLocaleString()}`);
      if (onCreated) onCreated(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record expenses');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="multi-person-expense-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={18} /> Split Expense Across People
          </DialogTitle>
          <DialogDescription>
            Pay for ID cards, fines, group insurance etc — split the total equally (or with per-person amounts) and record one expense per person.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category *</Label>
              <Input
                placeholder="e.g. ID Card Fee, Traffic Fine"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="mp-category"
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="mp-date" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Total Amount {overridePerPerson && <span className="text-xs text-muted-foreground ml-1">(disabled — per-person mode)</span>}</Label>
              <Input
                type="number" step="0.01" placeholder="0.00"
                value={totalAmount}
                onChange={(e) => setTotalAmount(e.target.value)}
                disabled={overridePerPerson}
                data-testid="mp-total"
              />
              {!overridePerPerson && total > 0 && eachShare > 0 && (
                <p className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1">
                  <Calculator size={11} /> SAR {eachShare.toLocaleString()} each × {total}
                </p>
              )}
            </div>
            <div>
              <Label>Payment Mode</Label>
              <Select value={paymentMode} onValueChange={setPaymentMode}>
                <SelectTrigger data-testid="mp-payment-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank">Bank</SelectItem>
                  <SelectItem value="credit">Credit</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="mp-override"
              checked={overridePerPerson}
              onCheckedChange={(v) => setOverridePerPerson(Boolean(v))}
              data-testid="mp-override-checkbox"
            />
            <Label htmlFor="mp-override" className="text-xs cursor-pointer">
              Enter per-person amounts instead of equal split
            </Label>
          </div>

          <div>
            <Label>Description</Label>
            <Input
              placeholder="e.g. Q2 2026 renewals"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div>
            <Input
              placeholder="Search employees or partners..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="mp-search"
            />
          </div>

          {/* Employees */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
              Employees ({empCount} picked)
            </h4>
            <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1" data-testid="mp-employee-list">
              {filteredEmployees.length === 0 ? (
                <p className="text-xs text-muted-foreground p-2">No employees</p>
              ) : filteredEmployees.map(e => (
                <div key={e.id} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-stone-50 dark:hover:bg-stone-800/50">
                  <Checkbox
                    id={`mp-emp-${e.id}`}
                    checked={!!pickedEmployees[e.id]}
                    onCheckedChange={() => togglePick('employee', e.id)}
                    data-testid={`mp-emp-${e.id}`}
                  />
                  <Label htmlFor={`mp-emp-${e.id}`} className="text-xs cursor-pointer flex-1">{e.label}</Label>
                  {overridePerPerson && pickedEmployees[e.id] && (
                    <Input
                      type="number" step="0.01" placeholder="0.00"
                      value={overrides[e.id] || ''}
                      onChange={(ev) => setOverrides(o => ({ ...o, [e.id]: ev.target.value }))}
                      className="h-7 w-24 text-xs"
                      data-testid={`mp-emp-amt-${e.id}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Partners — iter593: hidden unless the tenant flag is on */}
          {partnersEnabled && (
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
              Partners ({partnerCount} picked)
            </h4>
            <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-1" data-testid="mp-partner-list">
              {filteredPartners.length === 0 ? (
                <p className="text-xs text-muted-foreground p-2">No partners</p>
              ) : filteredPartners.map(p => (
                <div key={p.id} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-stone-50 dark:hover:bg-stone-800/50">
                  <Checkbox
                    id={`mp-partner-${p.id}`}
                    checked={!!pickedPartners[p.id]}
                    onCheckedChange={() => togglePick('partner', p.id)}
                    data-testid={`mp-partner-${p.id}`}
                  />
                  <Label htmlFor={`mp-partner-${p.id}`} className="text-xs cursor-pointer flex-1">{p.label}</Label>
                  {overridePerPerson && pickedPartners[p.id] && (
                    <Input
                      type="number" step="0.01" placeholder="0.00"
                      value={overrides[p.id] || ''}
                      onChange={(ev) => setOverrides(o => ({ ...o, [p.id]: ev.target.value }))}
                      className="h-7 w-24 text-xs"
                      data-testid={`mp-partner-amt-${p.id}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X size={14} className="mr-1" /> Cancel
          </Button>
          <Button onClick={submit} disabled={submitting || total === 0} data-testid="mp-submit">
            {submitting ? 'Recording…' : `Record ${total > 0 ? `${total} expense${total === 1 ? '' : 's'}` : 'expense'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
